import { API_BASE_URL } from '../constants/api';

const READ_STORAGE_KEY = 'announcement_read_ids';
const RUMUZ_PATTERN = /files\/download\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/i;
const RUMUZ_VALUE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const readListeners = new Set<() => void>();

export function formatAnnouncementDate(value: string | null | undefined): string {
  if (!value) return '';

  const match = value.match(/\/Date\((-?\d+)/);
  if (!match) return value;

  const date = new Date(parseInt(match[1], 10));
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function announcementDownloadUrl(rumuz: string): string {
  return `${API_BASE_URL}/files/download/${rumuz}`;
}

export function validAnnouncementRumuz(rumuz: string | null | undefined): string | null {
  return typeof rumuz === 'string' && RUMUZ_VALUE.test(rumuz) ? rumuz : null;
}

export function isServiceBulletinWithFile(announcement: { ServisBulteniMi?: boolean; DosyaRumuz?: string | null }): boolean {
  return announcement.ServisBulteniMi === true && validAnnouncementRumuz(announcement.DosyaRumuz) != null;
}

export function announcementDownloadLinkHtml(announcement: { ServisBulteniMi?: boolean; DosyaRumuz?: string | null }): string {
  const rumuz = validAnnouncementRumuz(announcement.DosyaRumuz);
  if (!rumuz) return '';

  const label = announcement.ServisBulteniMi
    ? 'Servis bültenini indirmek için tıklayınız'
    : 'Dosyayı indirmek için tıklayınız';

  return `<a href="${announcementDownloadUrl(rumuz)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
}

function visibleText(node: Node): string {
  return (node.textContent || '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/~+$/g, '')
    .trim();
}

function isDownloadAnchor(node: Node): boolean {
  return node.nodeType === Node.ELEMENT_NODE
    && (node as Element).tagName === 'A'
    && RUMUZ_PATTERN.test((node as Element).getAttribute('href') || '');
}

function hasMedia(node: Node): boolean {
  if (node.nodeType === Node.ELEMENT_NODE && (node as Element).matches('img[src], video[src], iframe[src]')) {
    return true;
  }
  if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    return (node as ParentNode).querySelector('img[src], video[src], iframe[src]') !== null;
  }
  return false;
}

/** <br>, boş etiket ve çeviri artıklarını açıklama sayma. */
function isIgnorable(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return visibleText(node).length === 0;
  if (node.nodeType !== Node.ELEMENT_NODE) return true;
  if ((node as Element).tagName === 'BR') return true;
  if (isDownloadAnchor(node) || hasMedia(node)) return false;
  return visibleText(node).length === 0;
}

function stripIgnorableEdges(parent: ParentNode): void {
  while (parent.firstChild && isIgnorable(parent.firstChild)) {
    parent.removeChild(parent.firstChild);
  }
  while (parent.lastChild && isIgnorable(parent.lastChild)) {
    parent.removeChild(parent.lastChild);
  }
}

function removeTranslationLeftovers(root: ParentNode): void {
  const leftovers = root.querySelectorAll('script, #gtx-trans, .gtx-trans-icon, [id*="gtx-trans"], [class*="gtx-trans"]');
  for (let i = 0; i < leftovers.length; i++) {
    leftovers[i].remove();
  }
}

function pruneEmptyMarkup(root: ParentNode): void {
  stripIgnorableEdges(root);
  const elements = root.querySelectorAll('*');
  for (let i = 0; i < elements.length; i++) {
    stripIgnorableEdges(elements[i]);
  }
  stripIgnorableEdges(root);
}

function removeDownloadAnchors(root: ParentNode): void {
  const anchors = root.querySelectorAll('a');
  for (let i = 0; i < anchors.length; i++) {
    if (RUMUZ_PATTERN.test(anchors[i].getAttribute('href') || '')) {
      anchors[i].remove();
    }
  }
}

/**
 * Açıklama HTML'ini temizler. İndirme bağlantısı burada üretilmez;
 * dosya varsa istemci DosyaRumuz ile kendi bağlantısını basar.
 * Metin veya görsel yoksa boş döner; <br> ve çeviri artığı yer kaplamaz.
 */
export function prepareAnnouncementHtml(html: string | null | undefined): string {
  if (!html || !html.trim() || typeof document === 'undefined') return '';

  const template = document.createElement('template');
  template.innerHTML = html;

  removeTranslationLeftovers(template.content);
  removeDownloadAnchors(template.content);
  pruneEmptyMarkup(template.content);

  if (!visibleText(template.content).length && !hasMedia(template.content)) {
    return '';
  }

  return template.innerHTML.trim();
}

export function announcementCardParts(announcement: { Duyuru?: string | null; ServisBulteniMi?: boolean; DosyaRumuz?: string | null }): { text: string; linkHtml: string } {
  const prepared = prepareAnnouncementHtml(announcement.Duyuru);
  let text = '';

  if (prepared && typeof document !== 'undefined') {
    const template = document.createElement('template');
    template.innerHTML = prepared;
    text = (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  return {
    text,
    linkHtml: announcementDownloadLinkHtml(announcement),
  };
}

export function downloadAnnouncementFile(rumuz: string): void {
  if (!RUMUZ_VALUE.test(rumuz)) return;
  window.open(announcementDownloadUrl(rumuz), '_blank', 'noopener,noreferrer');
}

export function getReadAnnouncementIds(): number[] {
  try {
    const raw = localStorage.getItem(READ_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => typeof id === 'number');
  } catch {
    return [];
  }
}

export function announcementIds(items: { Id?: number }[] | undefined, extraId?: number): number[] {
  const ids = (items ?? []).flatMap((item) => typeof item.Id === 'number' ? [item.Id] : []);
  if (typeof extraId === 'number' && !ids.includes(extraId)) ids.push(extraId);
  return ids;
}

export function rememberAnnouncementsRead(ids: number[]): void {
  const stored = new Set(getReadAnnouncementIds());
  ids.forEach((id) => {
    if (typeof id === 'number') stored.add(id);
  });
  localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(Array.from(stored)));
  readListeners.forEach((listener) => listener());
}

export function rememberAnnouncementRead(id: number): void {
  rememberAnnouncementsRead([id]);
}

export function subscribeReadAnnouncements(listener: () => void): () => void {
  readListeners.add(listener);
  return () => {
    readListeners.delete(listener);
  };
}
